import React, { useState, useMemo, useRef, useEffect } from "react";
import { indoorSportsApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./IndoorSports.scss";

const STATUSES = ["Active", "Under Maintenance", "Repair Required", "Closed"];
const CATEGORIES = ["Indoor", "Board Games", "Table Tennis", "Badminton", "Wrestling", "Other"];

const BLANK_FORM = {
  _id: null, Sport_Name: "", Category: "Indoor", Description: "",
  Courts_Areas_Available: "", Timing: "", Coach_In_Charge: "",
  Maintenance_Status: "Active", Maintenance_Note: "",
};

const IndoorSports = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState("");
  const [formData, setFormData] = useState(BLANK_FORM);
  const [selectedItem, setSelectedItem] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await apiClient(indoorSportsApi);
      const result = await response.json();
      if (response.ok) { setItems(result.data || []); }
    } catch (error) {
      console.error("Error fetching items:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleOutside = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    const closeDropdown = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("scroll", closeDropdown, true);
    window.addEventListener("resize", closeDropdown);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("scroll", closeDropdown, true);
      window.removeEventListener("resize", closeDropdown);
    };
  }, []);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 196 });
    setDropdownOpenId(id);
  };

  const openAddModal = () => { setFormData(BLANK_FORM); setModalType("add"); setDropdownOpenId(null); };
  const openEditModal = (item) => { setFormData({ ...item }); setModalType("edit"); setDropdownOpenId(null); };
  const openViewModal = (item) => { setSelectedItem(item); setModalType("view"); setDropdownOpenId(null); };
  const closeModal = () => { setModalType(""); setSelectedItem(null); setFormData(BLANK_FORM); };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? indoorSportsApi : `${indoorSportsApi}/${formData._id}`;
      const response = await apiClient(url, { method, body: JSON.stringify(formData) });
      if (response.ok) { fetchItems(); closeModal(); }
    } catch (error) {
      console.error("Error saving sport:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await apiClient(`${indoorSportsApi}/${id}`, { method: "DELETE" });
      fetchItems();
      setConfirmId(null); setDropdownOpenId(null);
    } catch (error) {
      console.error("Error deleting sport:", error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => items.filter(i => 
    (i.Sport_Name || "").toLowerCase().includes(searchText.toLowerCase()) || 
    (i.Coach_In_Charge || "").toLowerCase().includes(searchText.toLowerCase())
  ), [items, searchText]);

  const getStatusBadge = (status) => {
    const s = (status || "Active").toLowerCase().replace(/ /g, "-");
    return <span className={`is-maint-badge ${s}`}>{status}</span>;
  };

  return (
    <div className="is-page">
      <div className="is-header">
        <div className="is-header-left">
          <div className="is-header-icon">🏸</div>
          <div><h1>Indoor Sports</h1><p>Manage indoor game facilities, equipment, and training sessions.</p></div>
        </div>
        <button className="is-btn-primary" onClick={openAddModal}>Add Sport</button>
      </div>

      <div className="is-toolbar">
        <div className="is-search">🔍 <input type="text" placeholder="Search sports or coaches..." value={searchText} onChange={e => setSearchText(e.target.value)} /></div>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="is-grid">
        {filtered.map(item => (
          <div key={item._id} className="is-card" onClick={() => openViewModal(item)}>
            <div className="is-card-head">
              <div className="is-sport-icon">♟️</div>
              <div className="is-card-meta"><h3>{item.Sport_Name}</h3><span className="is-cat-tag">{item.Category}</span></div>
              <button className="is-dots" onClick={e => handleDotsClick(e, item._id)}>⋮</button>
            </div>
            <p className="is-card-desc">{item.Description}</p>
            <div className="is-card-info-row"><span>📍 {item.Courts_Areas_Available}</span><span>⏰ {item.Timing}</span></div>
            <div className="is-card-footer">{getStatusBadge(item.Maintenance_Status)}<span className="is-maint-note">{item.Maintenance_Note || "No maintenance notes"}</span></div>
          </div>
        ))}
      </div>

      {dropdownOpenId && (
        <div ref={dropdownRef} className="is-dropdown" style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}>
          <button onClick={() => openViewModal(items.find(i => i._id === dropdownOpenId))}>View Full Info</button>
          <button onClick={() => openEditModal(items.find(i => i._id === dropdownOpenId))}>Edit Detail</button>
          <div className="is-dd-divider"></div>
          <button className="is-dd-del" onClick={() => { setConfirmId(dropdownOpenId); setDropdownOpenId(null); }}>Delete Record</button>
        </div>
      )}

      {(modalType === "add" || modalType === "edit") && (
        <div className="is-overlay" onClick={closeModal}>
          <div className="is-form-modal" onClick={e => e.stopPropagation()}>
            <div className="is-modal-head">
              <div><h2>{modalType === "add" ? "Add" : "Edit"} Indoor Sport</h2><p>Provide facility details and maintenance status.</p></div>
              <button className="is-modal-close" onClick={closeModal}>×</button>
            </div>
            <form className="is-form" onSubmit={handleSave}>
              <div className="is-field full"><label>Sport Name <span className="req">*</span></label><input value={formData.Sport_Name} onChange={e => setFormData({...formData, Sport_Name: e.target.value})} required /></div>
              <div className="is-field-row">
                <div className="is-field"><label>Category</label><select value={formData.Category} onChange={e => setFormData({...formData, Category: e.target.value})}>{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                <div className="is-field"><label>Courts / Areas</label><input value={formData.Courts_Areas_Available} onChange={e => setFormData({...formData, Courts_Areas_Available: e.target.value})} /></div>
              </div>
              <div className="is-field-row">
                <div className="is-field"><label>Timing</label><input value={formData.Timing} onChange={e => setFormData({...formData, Timing: e.target.value})} /></div>
                <div className="is-field"><label>Coach In Charge</label><input value={formData.Coach_In_Charge} onChange={e => setFormData({...formData, Coach_In_Charge: e.target.value})} /></div>
              </div>
              <div className="is-field-row">
                <div className="is-field"><label>Maintenance Status</label><select value={formData.Maintenance_Status} onChange={e => setFormData({...formData, Maintenance_Status: e.target.value})}>{STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div className="is-field"><label>Maintenance Note</label><input value={formData.Maintenance_Note} onChange={e => setFormData({...formData, Maintenance_Note: e.target.value})} /></div>
              </div>
              <div className="is-field full"><label>Description <span className="req">*</span></label><textarea value={formData.Description} onChange={e => setFormData({...formData, Description: e.target.value})} required rows="3" /></div>
              <div className="is-form-footer"><button type="button" className="is-btn-light" onClick={closeModal}>Cancel</button><button type="submit" className="is-btn-primary">Save Sport</button></div>
            </form>
          </div>
        </div>
      )}

      {modalType === "view" && selectedItem && (
        <div className="is-overlay" onClick={closeModal}>
          <div className="is-view-modal" onClick={e => e.stopPropagation()}>
            <div className="ivm-head"><div className="ivm-icon">🏸</div><div><h2>{selectedItem.Sport_Name}</h2>{getStatusBadge(selectedItem.Maintenance_Status)}</div></div>
            <div className="ivm-body">
              <p className="ivm-desc">{selectedItem.Description}</p>
              <div className="ivm-info-grid">
                <div className="ivm-info-item"><span className="iig-label">Category</span><span className="iig-val">{selectedItem.Category}</span></div>
                <div className="ivm-info-item"><span className="iig-label">Courts</span><span className="iig-val">{selectedItem.Courts_Areas_Available}</span></div>
                <div className="ivm-info-item"><span className="iig-label">Timing</span><span className="iig-val">{selectedItem.Timing}</span></div>
                <div className="ivm-info-item"><span className="iig-label">Coach</span><span className="iig-val">{selectedItem.Coach_In_Charge}</span></div>
              </div>
              {selectedItem.Maintenance_Note && <div className="ivm-maint-preview"><strong>Note:</strong> <span>{selectedItem.Maintenance_Note}</span></div>}
            </div>
            <div className="ivm-footer"><button className="is-btn-light" onClick={closeModal}>Close</button><button className="is-btn-primary" onClick={() => openEditModal(selectedItem)}>Edit This</button></div>
          </div>
        </div>
      )}

      {confirmId && (
        <div className="is-overlay" onClick={() => setConfirmId(null)}>
          <div className="is-confirm" onClick={e => e.stopPropagation()}>
            <h3>Delete Sport Record?</h3>
            <p>This will remove the sport data from the dashboard.</p>
            <div className="is-confirm-actions"><button className="is-btn-light" onClick={() => setConfirmId(null)}>Cancel</button><button className="is-btn-danger" onClick={() => handleDelete(confirmId)}>Yes, Delete</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndoorSports;